import cn from 'classnames'
import {CSSProperties, FC, ReactNode} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Link} from '@/i18n'
import {theme} from '@/theme/theme'
import {QueryLink} from '@/utils/constants'
import {MOBILE_SCREENSIZE, USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {Text, TextFontWeight, TextVariant} from '../content/Text'

export type GetStartedBannerButtonType = {
  linkHref?: string | QueryLink
  text: string
  variant?: ButtonVariant
  textColor?: string
  fontWeight?: TextFontWeight
  icon?: ReactNode
  isIconAnimated?: boolean
  trackedEvent?: {name: string; parameters?: Record<string, string>}
  onClick?: () => void
}

type GetStartedBannerProps = {
  title: string
  titleVariant?: TextVariant
  description?: string
  buttons: GetStartedBannerButtonType[]
  buttonsOrientation?: 'vertical' | 'horizontal'
  backgroundColor?: string
  titleMaxWidth?: string
  hasBoxShadow?: boolean
  alignItems?: CSSProperties['alignItems']
  maxWidth?: string
}

export const GetStartedBanner: FC<React.PropsWithChildren<GetStartedBannerProps>> = ({
  title,
  titleVariant = TextVariant.h1,
  description,
  buttons,
  buttonsOrientation = 'vertical',
  backgroundColor = theme.palette.background.butter,
  titleMaxWidth,
  hasBoxShadow,
  children,
  alignItems = 'center',
  maxWidth = '495px',
}) => {
  return (
    <>
      <div className="wrapper">
        <div className="content">
          <div className="title">
            <Text variant={titleVariant} element="h2">
              {title}
            </Text>
          </div>
          {description && (
            <div className="description">
              <Text color={theme.palette.text.light}>{description}</Text>
            </div>
          )}
          <div className={cn('buttons', buttonsOrientation === 'horizontal' && 'horizontal')}>
            {buttons.map(
              ({
                linkHref,
                text,
                variant,
                textColor,
                fontWeight,
                icon,
                isIconAnimated,
                trackedEvent,
                onClick,
              }) => {
                const handleClick = () => {
                  onClick?.()
                  if (trackedEvent) {
                    analytics.track(trackedEvent.name, trackedEvent.parameters)
                  }
                }

                return (
                  <div key={text} className="button-wrapper">
                    {linkHref ? (
                      <Link href={linkHref} onClick={handleClick}>
                        <Button
                          variant={variant ?? ButtonVariant.primary}
                          textColor={textColor}
                          fontWeight={fontWeight}
                          icon={icon}
                          type="button"
                          isHoverable={variant === ButtonVariant.default}
                          fullWidth
                          isIconAnimated={isIconAnimated}
                        >
                          {text}
                        </Button>
                      </Link>
                    ) : (
                      <Button
                        onClick={handleClick}
                        variant={variant ?? ButtonVariant.primary}
                        textColor={textColor}
                        fontWeight={fontWeight}
                        icon={icon}
                        type="button"
                        isHoverable={variant === ButtonVariant.default}
                        fullWidth
                        isIconAnimated={isIconAnimated}
                      >
                        {text}
                      </Button>
                    )}
                  </div>
                )
              },
            )}
          </div>
        </div>
        {children}
      </div>

      <style jsx>{`
        .wrapper {
          display: flex;
          justify-content: space-between;
          align-items: ${alignItems};
          padding: ${theme.spacing[7]};
          background-color: ${backgroundColor};
          border-radius: 16px;
          ${hasBoxShadow ? `box-shadow: 0 15px 50px 0 rgba(229, 106, 97, 0.15);` : ''};
        }

        .description {
          margin-top: ${theme.spacing[2]};
          margin-bottom: -${theme.spacing[3]};
        }

        .content {
          flex: 1;
          max-width: ${maxWidth};
        }

        .buttons {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: ${theme.spacing[2.5]};
          margin-top: ${theme.spacing[5]};
        }

        .horizontal {
          flex-direction: row;
          flex-wrap: wrap;
        }

        .title {
          ${titleMaxWidth ? `max-width: ${titleMaxWidth}` : ''};
        }

        @media ${USER_TABLET_SCREENSIZE} {
          .wrapper {
            flex-direction: column;
            align-items: center;
            padding: ${theme.spacing[6]};
          }

          .content {
            text-align: center;
          }

          .buttons {
            align-items: center;
            justify-content: center;
          }

          .title {
            ${titleMaxWidth ? `max-width: 100%` : ''};
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .wrapper {
            padding: ${theme.spacing[6]} ${theme.spacing[3]};
          }

          .description {
            margin-bottom: -${theme.spacing[2]};
          }

          .buttons {
            display: block;
          }

          .button-wrapper + .button-wrapper {
            margin-top: ${theme.spacing[3]};
          }
        }
      `}</style>
    </>
  )
}
